/**
 * DriftInspectorDrawer — right-side drawer showing the full drift report for a
 * proposal revision.
 *
 * PR 6b Phase 2. Role-aware:
 *   - "admin"     → sees cost, rev3, name fields
 *   - "comercial" → sees rev3, name only — cost is NEVER rendered
 *
 * Strategy actions at the bottom of each compound group:
 *   accept_current      — adopt live catalog values
 *   create_new_revision — copy proposal into a new revision with live values
 *   keep_snapshot       — record an explicit "accepted as-is" decision
 *
 * Accessibility: focus trap via MUI Drawer, ESC closes (onClose), aria-label
 * on drawer root, close button with aria-label.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { fetchDrift, refreshDrift, resolveDrift } from "../../helpers/driftApi";
import { defaultMessageFor } from "../../helpers/apiErrors";
import type { ApiError } from "../../helpers/apiErrors";
import type {
  DriftItem,
  DriftReport,
  DriftSeverity,
  ResolveDriftResponse,
  ResolveDriftStrategy,
} from "../../model/Proposal";

// ── Props ────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  orderId: string;
  revisionVersion: number;
  role: "admin" | "comercial";
  onResolved: (response: ResolveDriftResponse) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCentValue(val: number | string | undefined): string {
  if (val === undefined || val === null) return "—";
  const num = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(num)) return String(val);
  return (num / 100).toFixed(2) + " €";
}

function formatDelta(delta: number | undefined): string {
  if (delta === undefined || delta === null) return "";
  const pct = delta * 100;
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

const FIELD_LABELS: Record<string, string> = {
  cost: "Custo",
  rev3: "Rev3",
  name: "Nome",
};

function severityColor(
  sev: DriftSeverity,
): "warning" | "error" | "default" | "info" {
  switch (sev) {
    case "minor":
      return "warning";
    case "major":
      return "error";
    case "orphan":
      return "error";
    case "indeterminate":
      return "default";
  }
}

function severityLabel(sev: DriftSeverity): string {
  return sev === "indeterminate" ? "?" : "Drift";
}

// ── Sub-components ───────────────────────────────────────────────────────────

interface DriftItemRowProps {
  item: DriftItem;
  role: "admin" | "comercial";
}

const DriftItemRow: React.FC<DriftItemRowProps> = ({ item, role }) => {
  // Role filter: comercial never sees cost field
  if (item.field === "cost" && role !== "admin") return null;

  const isOrphanOrIndet =
    item.severity === "orphan" || item.severity === "indeterminate";

  let deltaDisplay: React.ReactNode;
  if (item.severity === "orphan") {
    deltaDisplay = (
      <Typography variant="caption" color="text.secondary">
        Item não encontrado no catálogo
      </Typography>
    );
  } else if (item.severity === "indeterminate") {
    deltaDisplay = (
      <Typography variant="caption" color="text.secondary">
        Indeterminado
      </Typography>
    );
  } else if (item.field === "name") {
    // name drift: show old → new text, no delta_pct
    deltaDisplay = (
      <Typography variant="caption" color="text.secondary">
        {String(item.snapshot_val)} → {String(item.current_val ?? "—")}
      </Typography>
    );
  } else {
    const delta = formatDelta(item.delta_pct);
    deltaDisplay = (
      <Typography
        variant="caption"
        color={
          item.severity === "major"
            ? "error.main"
            : item.severity === "minor"
              ? "warning.main"
              : "text.secondary"
        }
        sx={{ fontWeight: 600 }}
      >
        {formatCentValue(item.snapshot_val)} →{" "}
        {formatCentValue(item.current_val)}{" "}
        {delta && (
          <span>
            (
            <span
              style={{
                color:
                  item.delta_pct! > 0
                    ? "#B45309"
                    : item.delta_pct! < 0
                      ? "#0369A1"
                      : undefined,
              }}
            >
              {delta}
            </span>
            )
          </span>
        )}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        py: 0.5,
      }}
    >
      <Chip
        label={severityLabel(item.severity)}
        size="small"
        color={severityColor(item.severity)}
        variant={item.severity === "orphan" ? "outlined" : "filled"}
        sx={{ minWidth: 48, flexShrink: 0 }}
      />
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>
          {FIELD_LABELS[item.field] ?? item.field}
          {!isOrphanOrIndet && ":"}
        </Typography>
        {deltaDisplay}
      </Box>
    </Box>
  );
};

// ── Resolve buttons ──────────────────────────────────────────────────────────

interface ResolveActionsProps {
  orderId: string;
  resolving: boolean;
  onResolve: (strategy: ResolveDriftStrategy) => void;
}

const ResolveActions: React.FC<ResolveActionsProps> = ({
  resolving,
  onResolve,
}) => (
  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
    <Button
      size="small"
      variant="outlined"
      color="success"
      disabled={resolving}
      onClick={() => onResolve("accept_current")}
      aria-label="Aceitar valores actuais do catálogo"
    >
      {resolving ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
      Aceitar valores actuais
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="primary"
      disabled={resolving}
      onClick={() => onResolve("create_new_revision")}
      aria-label="Criar nova revisão com valores actuais"
    >
      Criar nova revisão
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="warning"
      disabled={resolving}
      onClick={() => onResolve("keep_snapshot")}
      aria-label="Manter valores do snapshot actual"
    >
      Manter snapshot
    </Button>
  </Stack>
);

// ── Main drawer ──────────────────────────────────────────────────────────────

type LoadState = "idle" | "loading" | "success" | "error";

const DriftInspectorDrawer: React.FC<Props> = ({
  open,
  onClose,
  orderId,
  revisionVersion,
  role,
  onResolved,
}) => {
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [report, setReport] = useState<DriftReport | null>(null);
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    setLoadState("loading");
    setApiError(null);
    setResolveError(null);
    try {
      const data = await fetchDrift(orderId);
      setReport(data);
      setLoadState("success");
    } catch (err) {
      setApiError(err as ApiError);
      setLoadState("error");
    }
  }, [orderId]);

  // Fetch when drawer opens
  useEffect(() => {
    if (open) {
      load();
    } else {
      // Reset on close so next open shows fresh loading state
      setLoadState("idle");
      setReport(null);
      setApiError(null);
      setResolveError(null);
    }
  }, [open, load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setApiError(null);
    try {
      const data = await refreshDrift(orderId);
      setReport(data);
      setLoadState("success");
    } catch (err) {
      setApiError(err as ApiError);
      setLoadState("error");
    } finally {
      setRefreshing(false);
    }
  };

  const handleResolve = async (strategy: ResolveDriftStrategy) => {
    setResolving(true);
    setResolveError(null);
    try {
      const res = await resolveDrift(orderId, { strategy });
      onResolved(res);
    } catch (err) {
      setResolveError(err as ApiError);
    } finally {
      setResolving(false);
    }
  };

  // Group drift items by compound_id
  const grouped: Map<string, DriftItem[]> = new Map();
  if (report?.items) {
    for (const item of report.items) {
      const arr = grouped.get(item.compound_id) ?? [];
      arr.push(item);
      grouped.set(item.compound_id, arr);
    }
  }

  // ── Drawer content ──────────────────────────────────────────────────────────

  const renderContent = () => {
    if (loadState === "loading") {
      return (
        <Stack spacing={2} sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={48} />
          <Skeleton variant="rectangular" height={48} />
          <Skeleton variant="rectangular" height={48} />
        </Stack>
      );
    }

    if (loadState === "error" && apiError) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert
            severity="error"
            action={
              <Button
                size="small"
                color="inherit"
                onClick={load}
                aria-label="Tentar novamente carregar drift"
              >
                Tentar novamente
              </Button>
            }
          >
            {defaultMessageFor(apiError)}
          </Alert>
        </Box>
      );
    }

    if (loadState === "success" && report) {
      if (report.items.length === 0) {
        return (
          <Box sx={{ p: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Sem drift detectado — todos os preços estão actualizados.
            </Alert>
            <Button
              startIcon={<RefreshIcon />}
              size="small"
              variant="outlined"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label="Refrescar análise de drift"
            >
              {refreshing ? "A refrescar…" : "Refrescar"}
            </Button>
          </Box>
        );
      }

      return (
        <Box sx={{ p: 2 }}>
          {Array.from(grouped.entries()).map(([compoundId, items], idx) => (
            <Box key={compoundId}>
              {idx > 0 && <Divider sx={{ my: 1.5 }} />}
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mb: 0.5, fontFamily: "monospace" }}
              >
                {compoundId}
              </Typography>
              <Stack spacing={0.25}>
                {items.map((item, i) => (
                  <DriftItemRow
                    key={`${item.field}-${i}`}
                    item={item}
                    role={role}
                  />
                ))}
              </Stack>
              <ResolveActions
                orderId={orderId}
                resolving={resolving}
                onResolve={handleResolve}
              />
            </Box>
          ))}

          {resolveError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {defaultMessageFor(resolveError)}
            </Alert>
          )}
        </Box>
      );
    }

    return null;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      aria-label="Inspetor de drift"
      PaperProps={{
        sx: { width: 480, display: "flex", flexDirection: "column" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="h2">
            Inspetor de Drift
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Ordem {orderId} · Revisão {revisionVersion}
          </Typography>
        </Box>
        <Tooltip title="Refrescar">
          <span>
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={refreshing || loadState === "loading"}
              aria-label="Refrescar análise de drift"
              sx={{ mr: 0.5 }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Fechar inspetor de drift"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body — scrollable */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>{renderContent()}</Box>
    </Drawer>
  );
};

export default DriftInspectorDrawer;
